import {
  Box,
  Flex,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import Header from '@pages/ExamPage/Header'
import PracticeExam from '@pages/ExamPage/components/PracticeExam'
import useExam from '@pages/ExamPage/useExam'
import {useNavigate} from 'react-router-dom'
import {PATH} from '@utils/Const'
import {EXAM_MODE_TYPE} from '@pages/ExamPage/examPageModel'
import {CommonColors} from '@utils/CommonStyles'
import {Pagination, Radio} from 'antd'
import CardTopic from '@components/CardTopic'
import {useEffect, useState, useCallback} from 'react'
import {ReactComponent as SearchIcon} from '@assets/svgs/search.svg'
import appRequest from '@apis/app'
import Loading from '@components/Loading'
import debounce from 'lodash/debounce'
import ExamModal from './components/ExamModal'
import {useDispatch} from 'react-redux'
import {
  updateEntryPointShowUpgradeModal,
  updateIsShowUpgradeModal,
} from '@redux/map/mapSlice'

const ExamPage = () => {
  const navigate = useNavigate()
  const {exam} = useExam()
  const [inputValue, setInputValue] = useState('')
  const [searchParams, setSearchParams] = useState({
    part: 'PART_ONE',
    keyword: '',
    page: 1,
  })
  const [lessonTopics, setLessonTopics] = useState<any>()
  const [isLoadingTopics, setIsLoadingTopics] = useState(false)
  const [forecastSet, setForecastSet] = useState<any>(null)
  const {isOpen, onOpen, onClose} = useDisclosure()
  const [currentTestId, setCurrentTestId] = useState<any>(null)
  const dispatch: any = useDispatch()

  useEffect(() => {
    const getLessonTopics = async () => {
      setIsLoadingTopics(true)
      try {
        const res = await appRequest.getLessonTopics({
          part_type: searchParams.part,
          page: searchParams.page,
          keyword: searchParams.keyword,
        })
        setLessonTopics(res?.data?.data)
      } catch (error) {
      } finally {
        setIsLoadingTopics(false)
      }
    }

    getLessonTopics()
  }, [searchParams.keyword, searchParams.part, searchParams.page])

  useEffect(() => {
    const getForecastSet = async () => {
      try {
        const res = await appRequest.getForecastSet()
        setForecastSet(res?.data?.data)
      } catch (error) {}
    }

    getForecastSet()
  }, [])

  const handleGoToExamRoom = () => {
    if (exam?.data) {
      navigate(PATH.EXAM_ROOM, {
        state: {
          testId: exam?.data?.demo_test_id,
          type: EXAM_MODE_TYPE.PRACTICE,
        },
      })
    }
  }

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchParams(prev => ({
        ...prev,
        keyword: value,
        page: 1,
      }))
    }, 500),
    [],
  )

  const handleSearch = (e: any) => {
    const value = e.target.value
    setInputValue(value)
    debouncedSearch(value)
  }

  const handleClickTopic = (topic: any) => {
    if (topic?.is_lock) {
      dispatch(updateIsShowUpgradeModal(true))
      dispatch(updateEntryPointShowUpgradeModal('exam-page'))
      return
    }
    setCurrentTestId(topic.topic_id)
    onOpen()
  }

  return (
    <Box pt={{base: '16px', md: 0}}>
      <Header handleGoToExamRoom={handleGoToExamRoom} />
      <Box p={{base: '16px', md: '32px'}}>
        <Box maxW={'1400px'} mx={'auto'}>
          <Box
            p={{base: '16px', md: '32px'}}
            borderRadius={'16px'}
            bg={CommonColors.white}>
            <Text
              fontSize={{base: '16px', md: '24px'}}
              fontWeight={'600'}
              mb={{base: '16px', md: '32px'}}>
              Luyện thi theo chủ đề
            </Text>
            <Flex
              flexDirection={{base: 'column', lg: 'row'}}
              alignItems={'center'}
              gap={'16px'}
              justifyContent={'space-between'}
              sx={{
                '.ant-radio-button-wrapper-checked': {
                  backgroundColor: CommonColors.primary + ' !important',
                  borderColor: CommonColors.primary + ' !important',
                  color: '#fff',
                },
              }}>
              <Radio.Group
                onChange={e =>
                  setSearchParams({
                    ...searchParams,
                    part: e.target.value,
                    page: 1,
                    keyword: '',
                  })
                }
                value={searchParams.part}
                buttonStyle="solid"
                style={{
                  fontWeight: '600',
                }}>
                <Radio.Button value="PART_ONE">Part 1</Radio.Button>
                <Radio.Button value="PART_TWO">Part 2</Radio.Button>
                <Radio.Button value="PART_THREE">Part 3</Radio.Button>
              </Radio.Group>
              <InputGroup w={'270px'}>
                <InputLeftElement height={'100%'}>
                  <SearchIcon
                    color={'#7C7C7C'}
                    height={'20px'}
                    width={'20px'}
                  />
                </InputLeftElement>
                <Input
                  borderRadius={'4px'}
                  placeholder="Search"
                  size="sm"
                  value={inputValue}
                  onChange={handleSearch}
                />
              </InputGroup>
            </Flex>
            <Grid
              position={'relative'}
              templateColumns={{
                base: 'repeat(1, 1fr)',
                lg: 'repeat(2, 1fr)',
                xl: 'repeat(3, 1fr)',
              }}
              gap={{base: '16px', md: '32px'}}
              mt={{base: '16px', md: '32px'}}>
              {isLoadingTopics && <Loading />}
              {lessonTopics?.tests?.map((topic: any, index: any) => (
                <GridItem key={index}>
                  <CardTopic
                    title={topic.title_en}
                    subtitle={topic.title_vi}
                    image={topic.thumbnail}
                    completedLesson={topic.completed_lesson}
                    totalLesson={topic.total_lesson}
                    progress={topic.band ? (topic.band / 9) * 100 : 0}
                    type="exam"
                    band={topic.band}
                    onClick={() => {
                      handleClickTopic(topic)
                    }}
                  />
                </GridItem>
              ))}
            </Grid>
            <Flex
              justifyContent={'center'}
              mt={'32px'}
              sx={{
                '.ant-pagination-item': {
                  borderRadius: '4px !important',
                  border: '1px solid #E9E9E9 !important',
                },
                '.ant-pagination-item-active': {
                  backgroundColor: CommonColors.primary + ' !important',
                  borderColor: CommonColors.primary + ' !important',
                },
                '.ant-pagination-item-active a': {
                  color: '#fff !important',
                  fontWeight: '700 !important',
                },
                '.ant-pagination-item-link': {
                  border: '1px solid #E9E9E9 !important',
                },
              }}>
              <Pagination
                defaultCurrent={1}
                current={searchParams.page}
                defaultPageSize={6}
                total={lessonTopics?.total_topic}
                onChange={page =>
                  setSearchParams({
                    ...searchParams,
                    page: page,
                  })
                }
              />
            </Flex>
          </Box>
          <Box
            borderRadius={'16px'}
            p={{base: '16px', md: '32px'}}
            bg={CommonColors.white}
            mt={{base: '16px', md: '32px'}}>
            <Text
              fontSize={{base: '16px', md: '24px'}}
              fontWeight={'600'}
              mb={{base: '16px', md: '32px'}}>
              Luyện thi theo bộ đề
            </Text>
            <PracticeExam data={forecastSet} />
          </Box>
        </Box>
      </Box>
      <ExamModal isOpen={isOpen} onClose={onClose} testId={currentTestId} />
    </Box>
  )
}

export default ExamPage
